// chatContext/courseVideoControlStore.ts
//
// The course video on screen, for the companion's "Watch again" chip
// (ORA-1055). The chip renders inside a memoised answer on the far side of the
// tree from the course step, so — like `chatAppContextStore` — the two meet in
// a module store rather than a provider: the mounted course video step
// registers its player here, and the chip reads it at click time.

import { useSyncExternalStore } from 'react';

export interface CourseVideoControl {
  videoId: string;
  seekTo: (seconds: number) => void;
  play: () => void;
}

let active: CourseVideoControl | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const hasActive = (): boolean => active !== null;

/**
 * Make `control` the course video on screen. The returned function takes it
 * back, and only it: a step that unmounts after the next one registered leaves
 * the newer registration alone.
 */
export const registerCourseVideoControl = (
  control: CourseVideoControl
): (() => void) => {
  active = control;
  emit();

  return () => {
    if (active !== control) return;
    active = null;
    emit();
  };
};

/**
 * Seek the course video on screen to `startSec` and play it. False when no
 * course video is mounted, so the caller can say so instead of doing nothing.
 */
export const seekActiveCourseVideo = (startSec: number): boolean => {
  if (!active || !Number.isFinite(startSec)) return false;

  active.seekTo(Math.max(0, startSec));
  active.play();
  return true;
};

/** Whether a course video step is on screen, re-rendering when that changes. */
export const useHasActiveCourseVideo = (): boolean =>
  useSyncExternalStore(subscribe, hasActive, hasActive);

/** Test helper: forget the registration. */
export const resetCourseVideoControl = (): void => {
  if (!active) return;
  active = null;
  emit();
};
